import { b as _arrayLikeToArray, a as _unsupportedIterableToArray, _ as _objectSpread2 } from './unsupportedIterableToArray-4cf0be12.esm.js';
import * as tokens from '@hover/tokens-depth';
import * as bauhausTokens from '@hover/tokens-depth/bauhaus';
import * as tokens$2 from '@hover/tokens-device';
import * as tokens$3 from '@hover/tokens-color';
import _map from 'ramda/src/map';
import * as tokens$4 from '@hover/tokens-motion';
import * as tokens$1 from '@hover/tokens-border';

function _arrayWithoutHoles(arr) {
  if (Array.isArray(arr)) return _arrayLikeToArray(arr);
}

function _iterableToArray(iter) {
  if (typeof Symbol !== "undefined" && iter[Symbol.iterator] != null || iter["@@iterator"] != null) return Array.from(iter);
}

function _nonIterableSpread() {
  throw new TypeError("Invalid attempt to spread non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}

function _toConsumableArray(arr) {
  return _arrayWithoutHoles(arr) || _iterableToArray(arr) || _unsupportedIterableToArray(arr) || _nonIterableSpread();
}

/**
 * Generates a `box-shadow` CSS property from a shadow preset
 *
 * @example
 * boxShadow('#000', 0.75, '1px', '1px', '3px');
 * // '1px 1px 3px rgba(0, 0, 0, 0.25)'
 *
 * @param color - shadow color
 * @param opacity - opacity to be applied to shadow color
 * @param values - remaining shadow values in the same order as CSS spec
 *
 * @returns `box-shadow` CSS property value
 */
var boxShadow = function boxShadow() {
  for (var _len = arguments.length, shadow = new Array(_len), _key = 0; _key < _len; _key++) {
    shadow[_key] = arguments[_key];
  }
  return shadow.join(' ');
};
var insetBoxShadow = function insetBoxShadow() {
  return "inset ".concat(boxShadow.apply(void 0, arguments));
};

/**
 * Generates a composite `box-shadow` CSS
 * property from an array of shadow presets
 *
 * @example
 * boxShadow([
 *   ['#000', 0.75, '1px', '1px', '3px'],
 *   ['#333', 0.25, '2px', '2px', '5px']
 * ]);
 *
 * // '1px 1px 3px rgba(0, 0, 0, 0.25), 1px 1px 3px rgba(51, 51, 51, 0.75)'
 *
 * @param shadows shadow presets
 *
 * @returns `box-shadow` CSS property value
 */
var boxShadows = function boxShadows(shadows) {
  var inset = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false;
  return shadows.map(function (s) {
    return (inset ? insetBoxShadow : boxShadow).apply(void 0, _toConsumableArray(s));
  }).join(',');
};
/**
 * Retrieve a shadow preset from depth tokens
 *
 * @example
 * // Retrieves tokens `distance500Color`, `distance500Opacity`, etc.
 * getShadowPresetFromTokens({ name: 'distance500' })
 * // ['0px', 0.4, '0px', '2px', '6px', '0px']
 *
 *
 * @param name shadow name (e.g: `'500'` → `'distance500'`)
 * @returns shadow preset
 */
var getShadowPresetFromTokens = function getShadowPresetFromTokens(_ref) {
  var name = _ref.name,
    color = _ref.color,
    _ref$isBauhaus = _ref.isBauhaus,
    isBauhaus = _ref$isBauhaus === void 0 ? false : _ref$isBauhaus;
  var offsetX = "".concat(name, "OffsetX");
  var offsetY = "".concat(name, "OffsetY");
  var blurRadius = "".concat(name, "BlurRadius");
  var spreadRadius = "".concat(name, "SpreadRadius");
  var themedTokens = isBauhaus ? bauhausTokens : tokens;
  return [themedTokens[offsetX], themedTokens[offsetY], themedTokens[blurRadius], themedTokens[spreadRadius], // NOTE: originally this function was type-safe against the existing tokens
  // but since we decoupled colors that's not the case anymore. The public
  // function (`getBoxShadowProperties`) is, however, so this assertion should
  // be fine. The `getBoxShadowProperties` function will require a `color` for
  // any shadow that does have color defined in the tokens.
  color !== null && color !== void 0 ? color : themedTokens["".concat(name, "Color")]];
};
/**
 * Retrieve a CSS `box-shadow` value from depth tokens. A `color` option is
 * required if no color is defined in the tokens.
 *
 * @example
 * // Retrieves shadow for `distance300A`
 * getShadowProperties({ name: 'distance300A' }')
 * // "0px 0.4px 0.5px 0px rgba(0,0,0,0.07)"
 *
 * // Retrieves shadow combining `distance300A` and `distance300B`
 * getShadowProperties({ name: ['distance300A', 'distance300B'] }')
 * // ""0px 0.4px 0.5px 0px rgba(0,0,0,0.07),0px 1.2px 1.3px -0.8px rgba(0,0,0,0.07)"
 *
 * // Retrieves `halo` shadow with a required color
 * getShadowProperties({ name: 'halo', color: 'red' })
 *
 * @param name shadow name (e.g: `'500'` → `'distance500'`)
 * @returns shadow preset
 */
var getBoxShadowFromTokens = function getBoxShadowFromTokens(options) {
  var name = options.name,
    inset = options.inset,
    isBauhaus = options.isBauhaus;
  var withColor = function withColor(o) {
    return 'color' in options && options.color ? _objectSpread2(_objectSpread2({}, o), {}, {
      color: options.color
    }) : o;
  };
  return Array.isArray(name) ? boxShadows(name.map(function (n) {
    return getShadowPresetFromTokens(_objectSpread2({
      isBauhaus: isBauhaus
    }, withColor({
      name: n
    })));
  }), inset) : (inset ? insetBoxShadow : boxShadow).apply(void 0, _toConsumableArray(getShadowPresetFromTokens(_objectSpread2({
    isBauhaus: isBauhaus
  }, withColor({
    name: name
  })))));
};

var borderWidths = {
  0: tokens$1.width0,
  400: tokens$1.width400,
  500: tokens$1.width500
};

// tl;dr — prefix numeric keys, i.e: { 100: string } → { width100: string }
//
// Chakra and other styled system implementations convert breakpoints to a
// "hybrid" array + object structure and numeric keys are treated as array
// indices in that case (even though they are actually string, e.g: '100').
// This can cause super weird behavior when said implementation relies on
// sorting the array in order to apply breakpoints in the correct order.
// Thanks a lot JavaScript 🥴

/**
 * Similar to Chakra's `ResponsiveValue` for typing responsive style props, but
 * strictly for system breakpoints (and without the array-style responsive prop)
 */

var breakpoints = {
  width100: tokens$2.breakpointWidth100,
  width200: tokens$2.breakpointWidth200,
  width250: tokens$2.breakpointWidth250,
  width275: tokens$2.breakpointWidth275,
  width300: tokens$2.breakpointWidth300,
  width350: tokens$2.breakpointWidth350,
  width400: tokens$2.breakpointWidth400,
  width500: tokens$2.breakpointWidth500,
  desktop: tokens$2.breakpointWidthDesktop,
  mobile: tokens$2.breakpointWidthMobile,
  tablet: tokens$2.breakpointWidthTablet
};

var colors = {
  primary: {
    50: tokens$3.primary50,
    100: tokens$3.primary100,
    200: tokens$3.primary200,
    300: tokens$3.primary300,
    400: tokens$3.primary400,
    500: tokens$3.primary500,
    600: tokens$3.primary600,
    700: tokens$3.primary700,
    800: tokens$3.primary800,
    900: tokens$3.primary900,
    base: tokens$3.primaryBase
  },
  secondary: {
    100: tokens$3.secondary100,
    200: tokens$3.secondary200,
    300: tokens$3.secondary300,
    400: tokens$3.secondary400,
    500: tokens$3.secondary500,
    600: tokens$3.secondary600,
    700: tokens$3.secondary700,
    800: tokens$3.secondary800,
    900: tokens$3.secondary900,
    base: tokens$3.secondaryBase
  },
  neutral: {
    0: tokens$3.neutral0,
    50: tokens$3.neutral50,
    100: tokens$3.neutral100,
    200: tokens$3.neutral200,
    300: tokens$3.neutral300,
    400: tokens$3.neutral400,
    500: tokens$3.neutral500,
    550: tokens$3.neutral550,
    600: tokens$3.neutral600,
    700: tokens$3.neutral700,
    800: tokens$3.neutral800,
    900: tokens$3.neutral900,
    background: tokens$3.neutralBackground,
    backgroundDark: tokens$3.neutralBackgroundDark,
    disabled: tokens$3.neutralDisabled,
    text: tokens$3.neutralText,
    textLight: tokens$3.neutralTextLight
  },
  success: {
    50: tokens$3.success50,
    100: tokens$3.success100,
    200: tokens$3.success200,
    300: tokens$3.success300,
    400: tokens$3.success400,
    500: tokens$3.success500,
    600: tokens$3.success600,
    700: tokens$3.success700,
    800: tokens$3.success800,
    900: tokens$3.success900,
    base: tokens$3.successBase
  },
  warning: {
    50: tokens$3.warning50,
    100: tokens$3.warning100,
    200: tokens$3.warning200,
    300: tokens$3.warning300,
    400: tokens$3.warning400,
    500: tokens$3.warning500,
    600: tokens$3.warning600,
    700: tokens$3.warning700,
    800: tokens$3.warning800,
    900: tokens$3.warning900,
    base: tokens$3.warningBase
  },
  danger: {
    50: tokens$3.danger50,
    100: tokens$3.danger100,
    200: tokens$3.danger200,
    300: tokens$3.danger300,
    400: tokens$3.danger400,
    500: tokens$3.danger500,
    600: tokens$3.danger600,
    700: tokens$3.danger700,
    800: tokens$3.danger800,
    900: tokens$3.danger900,
    base: tokens$3.dangerBase
  },
  info: {
    50: tokens$3.primary50,
    100: tokens$3.primary100,
    200: tokens$3.primary200,
    300: tokens$3.primary300,
    400: tokens$3.primary400,
    500: tokens$3.primary500,
    600: tokens$3.primary600,
    700: tokens$3.primary700,
    800: tokens$3.primary800,
    900: tokens$3.primary900,
    base: tokens$3.primaryBase
  },
  brandNavy: {
    100: tokens$3.brandNavy100,
    200: tokens$3.brandNavy200,
    300: tokens$3.brandNavy300,
    400: tokens$3.brandNavy400,
    500: tokens$3.brandNavy500,
    600: tokens$3.brandNavy600,
    700: tokens$3.brandNavy700,
    800: tokens$3.brandNavy800,
    900: tokens$3.brandNavy900
  },
  brandGreen: {
    100: tokens$3.brandGreen100,
    200: tokens$3.brandGreen200,
    300: tokens$3.brandGreen300,
    400: tokens$3.brandGreen400,
    500: tokens$3.brandGreen500,
    600: tokens$3.brandGreen600,
    700: tokens$3.brandGreen700,
    800: tokens$3.brandGreen800,
    900: tokens$3.brandGreen900
  },
  brandBrown: {
    100: tokens$3.brandBrown100,
    200: tokens$3.brandBrown200,
    300: tokens$3.brandBrown300,
    400: tokens$3.brandBrown400,
    500: tokens$3.brandBrown500,
    600: tokens$3.brandBrown600,
    700: tokens$3.brandBrown700,
    800: tokens$3.brandBrown800,
    900: tokens$3.brandBrown900
  },
  brandRed: {
    100: tokens$3.brandRed100,
    200: tokens$3.brandRed200,
    300: tokens$3.brandRed300,
    400: tokens$3.brandRed400,
    500: tokens$3.brandRed500,
    600: tokens$3.brandRed600,
    700: tokens$3.brandRed700,
    800: tokens$3.brandRed800,
    900: tokens$3.brandRed900
  },
  brandTan: {
    100: tokens$3.brandTan100,
    200: tokens$3.brandTan200,
    300: tokens$3.brandTan300,
    400: tokens$3.brandTan400,
    500: tokens$3.brandTan500,
    600: tokens$3.brandTan600,
    700: tokens$3.brandTan700,
    800: tokens$3.brandTan800,
    900: tokens$3.brandTan900
  },
  brandOrange: {
    100: tokens$3.brandOrange100,
    200: tokens$3.brandOrange200,
    300: tokens$3.brandOrange300,
    400: tokens$3.brandOrange400,
    500: tokens$3.brandOrange500,
    600: tokens$3.brandOrange600,
    700: tokens$3.brandOrange700,
    800: tokens$3.brandOrange800,
    900: tokens$3.brandOrange900
  },
  brandYellow: {
    100: tokens$3.brandYellow100,
    200: tokens$3.brandYellow200,
    300: tokens$3.brandYellow300,
    400: tokens$3.brandYellow400,
    500: tokens$3.brandYellow500,
    600: tokens$3.brandYellow600,
    700: tokens$3.brandYellow700,
    800: tokens$3.brandYellow800,
    900: tokens$3.brandYellow900
  },
  brandPurple: {
    100: tokens$3.brandPurple100,
    200: tokens$3.brandPurple200,
    300: tokens$3.brandPurple300,
    400: tokens$3.brandPurple400,
    500: tokens$3.brandPurple500,
    600: tokens$3.brandPurple600,
    700: tokens$3.brandPurple700,
    800: tokens$3.brandPurple800,
    900: tokens$3.brandPurple900
  },
  lightAlpha: {
    50: tokens$3.lightAlpha50,
    100: tokens$3.lightAlpha100,
    200: tokens$3.lightAlpha200,
    300: tokens$3.lightAlpha300,
    400: tokens$3.lightAlpha400,
    500: tokens$3.lightAlpha600,
    600: tokens$3.lightAlpha600,
    700: tokens$3.lightAlpha700,
    800: tokens$3.lightAlpha800,
    900: tokens$3.lightAlpha900
  },
  darkAlpha: {
    50: tokens$3.darkAlpha50,
    100: tokens$3.darkAlpha100,
    200: tokens$3.darkAlpha200,
    300: tokens$3.darkAlpha300,
    400: tokens$3.darkAlpha400,
    500: tokens$3.darkAlpha600,
    600: tokens$3.darkAlpha600,
    700: tokens$3.darkAlpha700,
    800: tokens$3.darkAlpha800,
    900: tokens$3.darkAlpha900
  },
  overlayLight: tokens$3.overlayLight,
  overlayDark: tokens$3.overlayDark,
  halo: {
    primary: tokens$3.haloPrimary,
    neutral: tokens$3.haloNeutral,
    warning: tokens$3.haloWarning,
    danger: tokens$3.haloDanger,
    success: tokens$3.haloSuccess
  },
  ring: {
    primary: tokens$3.ringPrimary,
    neutral: tokens$3.ringNeutral
  },
  border: {
    danger: tokens$3.borderDanger,
    disabled: tokens$3.borderDisabled,
    focus: tokens$3.borderFocus,
    focusDanger: tokens$3.borderFocusDanger,
    focusNeutral: tokens$3.borderFocusNeutral,
    focusSuccess: tokens$3.borderFocusSuccess,
    focusWarning: tokens$3.borderFocusWarning,
    idle: tokens$3.borderIdle,
    invalid: tokens$3.borderInvalid,
    invalidFocus: tokens$3.borderInvalidFocus,
    neutral: tokens$3.borderNeutral,
    primary: tokens$3.borderPrimary
  }
};
var hues = [50, 100, 200, 300, 400, 500, 600, 700, 800, 900];
var neutralHues = [0, 100, 200, 300, 400, 500, 600, 700, 800, 900];
var neutralSchemes = ['light', 'dark'];
var productSchemes = ['primary', 'secondary', 'neutral', 'success', 'warning', 'danger', 'info'];
var brandSchemes = ['brandNavy', 'brandGreen', 'brandBrown', 'brandRed', 'brandTan', 'brandOrange', 'brandYellow'];
var palettes = /*#__PURE__*/[].concat(productSchemes, brandSchemes);
var schemes = /*#__PURE__*/[].concat( /*#__PURE__*/_toConsumableArray(palettes), neutralSchemes);

// Framer also supports 'tween' and 'inertia',
// but we can union those as/if they are needed

var fromEasingPreset = function fromEasingPreset(_ref) {
  var duration = _ref.duration,
    x1 = _ref.x1,
    y1 = _ref.y1,
    x2 = _ref.x2,
    y2 = _ref.y2;
  return "".concat(duration, " cubic-bezier(").concat(x1, ", ").concat(y1, ", ").concat(x2, ", ").concat(y2, ")");
};
var easingPresets = {
  150: {
    duration: tokens$4.easing150Duration,
    x1: tokens$4.easing150X1,
    y1: tokens$4.easing150Y1,
    x2: tokens$4.easing150X2,
    y2: tokens$4.easing150Y2
  },
  250: {
    duration: tokens$4.easing250Duration,
    x1: tokens$4.easing250X1,
    y1: tokens$4.easing250Y1,
    x2: tokens$4.easing250X2,
    y2: tokens$4.easing250Y2
  },
  500: {
    duration: tokens$4.easing500Duration,
    x1: tokens$4.easing500X1,
    y1: tokens$4.easing500Y1,
    x2: tokens$4.easing500X2,
    y2: tokens$4.easing500Y2
  }
};
var springPresets = {
  firm: {
    friction: tokens$4.springFirmFriction,
    mass: tokens$4.springFirmMass,
    tension: tokens$4.springFirmTension,
    velocity: tokens$4.springFirmVelocity
  }
};
var framerPresets = {
  snappy: {
    type: tokens$4.framerSnappyType,
    damping: tokens$4.framerSnappyDamping,
    mass: tokens$4.framerSnappyMass,
    stiffness: tokens$4.framerSnappyStiffness,
    velocity: tokens$4.framerSnappyVelocity
  },
  firm: {
    type: tokens$4.framerFirmType,
    damping: tokens$4.framerFirmDamping,
    mass: tokens$4.framerFirmMass,
    stiffness: tokens$4.framerFirmStiffness,
    velocity: tokens$4.framerFirmVelocity
  },
  tight: {
    type: tokens$4.framerTightType,
    damping: tokens$4.framerTightDamping,
    mass: tokens$4.framerTightMass,
    stiffness: tokens$4.framerTightStiffness,
    velocity: tokens$4.framerTightVelocity
  },
  soft: {
    type: tokens$4.framerSoftType,
    damping: tokens$4.framerSoftDamping,
    mass: tokens$4.framerSoftMass,
    stiffness: tokens$4.framerSoftStiffness,
    velocity: tokens$4.framerSoftVelocity
  }
};
var animation = {
  spin: {
    500: {
      duration: tokens$4.animationSpin500Duration
    },
    700: {
      duration: tokens$4.animationSpin700Duration
    }
  }
};
var easings = /*#__PURE__*/_map(fromEasingPreset, easingPresets);
var springs = springPresets;
var framer = framerPresets;
var motion = {
  animation: animation,
  easings: easings,
  springs: springs,
  framer: framer
};

var presets = ['danger', 'disabled', 'focus', 'focusDanger', 'focusNeutral', 'focusSuccess', 'focusWarning', 'idle', 'invalid', 'invalidFocus', 'neutral', 'primary'];
var widthPresets = {
  width0: /*#__PURE__*/"".concat(borderWidths[0], " solid"),
  width400: /*#__PURE__*/"".concat(borderWidths[400], " solid"),
  width500: /*#__PURE__*/"".concat(borderWidths[500], " solid")
};
var preset = function preset(color) {
  var width = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 400;
  var style = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : 'solid';
  return "".concat(borderWidths[width], " ").concat(style, " ").concat(color);
};

export { presets as a, borderWidths as b, breakpoints as c, palettes as d, brandSchemes as e, neutralSchemes as f, productSchemes as g, hues as h, getBoxShadowFromTokens as i, boxShadow as j, colors as k, motion as m, neutralHues as n, preset as p, schemes as s, widthPresets as w };
